import { BaseDragger } from "./BaseDragger";
import type { Point2D, Point3D } from "./types";

/**
 * Abstract base class for OneWay draggers.
 *
 * OneWay draggers create geometry by dragging from start to end point:
 * - Show preview overlay during drag
 * - Create actual entity only on drag end
 * - Support measurements display during drag
 *
 * Subclasses implement:
 * - createPreview(): Create initial preview overlay
 * - updatePreview(): Update preview during drag
 * - createEntity(): Create the actual ODA entity on end
 */
export abstract class OneWayDragger extends BaseDragger {
  protected previewIds: string[] = [];

  // ============== Dragger Callbacks ==============

  protected onStart(screen: Point2D, world: Point3D, _ev: PointerEvent): void {
    // Create initial preview
    this.createPreview(screen, world);
  }

  protected onDrag(screen: Point2D, world: Point3D, _ev: PointerEvent): void {
    // Update preview with current position
    this.updatePreview(screen, world);

    // Keep main canvas rendering during drag
    this.viewer.update();
  }

  protected onEnd(screen: Point2D, world: Point3D, _ev: PointerEvent): void {
    // Clear preview
    this.clearPreview();

    // Check minimum drag distance
    if (!this.hasMinimumDrag()) {
      this.clear();
      return;
    }

    // Create the actual entity
    this.createEntity(screen, world);

    // Update display
    this.viewer.update();

    // Clear state for next operation
    this.clear();
  }

  protected onCancel(): void {
    this.clearPreview();
    this.clear();
    this.viewer.update();
  }

  // ============== Abstract Methods ==============

  /**
   * Create the initial preview overlay.
   * Called on drag start.
   */
  protected abstract createPreview(screen: Point2D, world: Point3D): void;

  /**
   * Update the preview overlay during drag.
   * Called on every drag move.
   */
  protected abstract updatePreview(screen: Point2D, world: Point3D): void;

  /**
   * Create the actual ODA entity.
   * Called on drag end.
   */
  protected abstract createEntity(screen: Point2D, world: Point3D): void;

  // ============== Utility Methods ==============

  /**
   * Clear all preview overlays.
   */
  protected clearPreview(): void {
    const overlay = this.viewer.overlayManager;
    if (overlay) {
      for (const id of this.previewIds) {
        overlay.remove(id);
      }
    }
    this.previewIds = [];
  }

  /**
   * Check if drag distance meets minimum threshold.
   */
  protected hasMinimumDrag(): boolean {
    if (!this.startScreen || !this.currentScreen) {
      return false;
    }
    const dx = this.currentScreen.x - this.startScreen.x;
    const dy = this.currentScreen.y - this.startScreen.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance >= 5; // Minimum 5 pixels
  }

  /**
   * Get the bounding box from start to current position.
   * Returns normalized coordinates (min/max regardless of drag direction).
   */
  protected getBounds(): {
    screenMin: Point2D;
    screenMax: Point2D;
    worldMin: Point3D;
    worldMax: Point3D;
    width: number;
    height: number;
    worldWidth: number;
    worldHeight: number;
  } | null {
    if (
      !this.startScreen ||
      !this.currentScreen ||
      !this.startWorld ||
      !this.currentWorld
    ) {
      return null;
    }

    const screenMin: Point2D = {
      x: Math.min(this.startScreen.x, this.currentScreen.x),
      y: Math.min(this.startScreen.y, this.currentScreen.y),
    };
    const screenMax: Point2D = {
      x: Math.max(this.startScreen.x, this.currentScreen.x),
      y: Math.max(this.startScreen.y, this.currentScreen.y),
    };
    const worldMin: Point3D = {
      x: Math.min(this.startWorld.x, this.currentWorld.x),
      y: Math.min(this.startWorld.y, this.currentWorld.y),
      z: 0,
    };
    const worldMax: Point3D = {
      x: Math.max(this.startWorld.x, this.currentWorld.x),
      y: Math.max(this.startWorld.y, this.currentWorld.y),
      z: 0,
    };

    return {
      screenMin,
      screenMax,
      worldMin,
      worldMax,
      width: screenMax.x - screenMin.x,
      height: screenMax.y - screenMin.y,
      worldWidth: worldMax.x - worldMin.x,
      worldHeight: worldMax.y - worldMin.y,
    };
  }

  /**
   * Add a preview overlay ID to track for cleanup.
   */
  protected addPreviewId(id: string): void {
    this.previewIds.push(id);
  }

  protected clear(): void {
    super.clear();
    this.clearPreview();
  }
}
